import { call } from '@/lib/api';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

export type Account = {
  id: number;
  name: string;
  notes: string | null;
  createdByUserId: number;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdByUser: { id: number; email: string; name: string | null } | null;
};

export type AccountContactSummary = {
  id: number;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  memberUserIds: number[];
  createdAt: string;
};

export type AccountManager = {
  id: number;
  email: string;
  name: string | null;
};

export type AccountDetailsResponse = {
  account: Account;
  contacts: AccountContactSummary[];
  managers: AccountManager[];
};

export type AccountListRow = {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
  contactsCount: number;
};

export type AccountListFilters = {
  ownerUserId?: number[];
  search?: string;
  view?: 'mine' | 'all';
  page?: number;
  pageSize?: number;
  sort?: 'createdAt' | 'updatedAt' | 'name';
  order?: 'asc' | 'desc';
};

export type AccountListResponse = {
  rows: AccountListRow[];
  total: number;
  page: number;
  pageSize: number;
};

export const accountKeys = {
  all: ['accounts'] as const,
  lists: () => [...accountKeys.all, 'list'] as const,
  list: (f: AccountListFilters) => [...accountKeys.lists(), f] as const,
  details: () => [...accountKeys.all, 'detail'] as const,
  detail: (id: number) => [...accountKeys.details(), id] as const,
};

function serializeListFilters(f: AccountListFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.ownerUserId?.length) out.ownerUserId = f.ownerUserId.join(',');
  if (f.search) out.search = f.search;
  if (f.view) out.view = f.view;
  if (f.page) out.page = f.page;
  if (f.pageSize) out.pageSize = f.pageSize;
  if (f.sort) out.sort = f.sort;
  if (f.order) out.order = f.order;
  return out;
}

export function useAccountsList(filters: AccountListFilters) {
  return useQuery({
    queryKey: accountKeys.list(filters),
    queryFn: () =>
      callGet<AccountListResponse>(
        'accounts/list',
        serializeListFilters(filters),
      ),
    placeholderData: keepPreviousData,
  });
}

const callGet = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

export function useAccountDetails(id: number | null | undefined) {
  return useQuery({
    enabled: id != null,
    queryKey: accountKeys.detail(id ?? 0),
    queryFn: () => callGet<AccountDetailsResponse>('accounts/details', { id }),
  });
}
